import { Spinner } from 'design-system';
import { lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import FirstPartyPageview from '../components/Analytics/FirstPartyPageview.jsx';
import AdminRoute from '../components/Routes/AdminRoute';
import PrivateRoute from '../components/Routes/PrivateRoute';
import { closeAllOverlays } from '../design-system/overlayStore';
import { MESSENGER_V2_GROUP_INVITE_ACCEPT_PATH } from '../features/messenger-v2/messengerV2Routes.js';
import {
  buildCanonicalRoomAliasPath,
  getRoomBasePathAliases,
  resolveRoomLegacyTabTarget,
} from '../features/rooms/roomChatRoutes.js';
import AppShell from '../layouts/AppShell';
import {
  FOOTER_PAGE_SLUGS,
  getFindGraveSlug,
  getRoomsAcceptPath,
  getRoomsBasePath,
  getRoomsCreatePath,
  getRoomTabRoute,
} from '../service/constants';
import HomepageFindGrave from './LandingPage/atoms//FindGrave/HomepageFindGrave';

const Graves = lazy(() => import('../containers/Graves/Graves'));
const GraveClaimPage = lazy(
  () => import('../containers/NewDeceased/atoms/GraveClaimPage/GraveClaimPage'),
);
const DeceasedGraveWall = lazy(() => import('../containers/NewDeceased/DeceasedGraveWall'));
const Notifications = lazy(() => import('../containers/NewSettings/atoms//Notifications'));
const Contact = lazy(() => import('../containers/NewSettings/atoms/Contact'));
const PaymentMethods = lazy(() => import('../containers/NewSettings/atoms/PaymentMethods'));
const CureSubscription = lazy(
  () => import('../containers/NewSettings/atoms/CureSubscription/CureSubscription'),
);
const SettingsPersonalInformation = lazy(
  () => import('../containers/NewSettings/atoms/SettingsPagePersonalInformation'),
);
const UserAccount = lazy(() => import('../containers/NewSettings/atoms/UserAccount/UserAccount'));
const SettingsPageTabs = lazy(() => import('../containers/NewSettings/SettingsPageTabs'));
const SecuritySessions = lazy(() => import('../containers/NewSettings/atoms/Security/Sessions'));
const NotificationsPage = lazy(() => import('./Notifications/NotificationsPage'));
const Registration = lazy(() => import('../pages/Auth/Registration'));
const SignInPage = lazy(() => import('../pages/Auth/SignInPage'));
const Payments = lazy(() => import('../pages/Payments/Payments'));
const AccountConfirmation = lazy(() => import('./AccountConfirmation/AccountConfirmation'));
const MoonRequestPage = lazy(() => import('./MoonRequest/MoonRequestPage'));
const CreateNewGrave = lazy(() => import('./CreateNewGrave/CreateNewGrave'));
const FamilyTree = lazy(() => import('./FamilyTree/FamilyTree'));
const FamilyTreeGuestIntro = lazy(() => import('./FamilyTree/FamilyTreeGuestIntro'));
const Homepage = lazy(() => import('./NewHomepage/NewHomepage'));
const NotFoundPage = lazy(() => import('./404/404'));
const PublicNotice = lazy(() => import('./DeathNotice/PublicNotice'));
const FuneralRoom = lazy(() => import('./FuneralRoom/FuneralRoom'));
const HomepageGrave = lazy(() => import('./LandingPage/atoms/Grave/HomepageGrave'));
const HomepageInvestigate = lazy(
  () => import('./LandingPage/atoms/Investigate/HomepageInvestigate'),
);
const LearnMore = lazy(() => import('./LandingPage/atoms/LearnMore/LearnMore'));
const HomepageScientists = lazy(() => import('./LandingPage/atoms/Scientists/HomepageScientists'));
const TermsConditions = lazy(() => import('./LandingPage/TermsConditions/TermsConditions'));
const PrivacyPolicy = lazy(() => import('./LandingPage/TermsConditions/PrivacyPolicy'));
const CookiesPolicy = lazy(() => import('./LandingPage/TermsConditions/CookiesPolicy'));
const AboutNettgrav = lazy(() => import('./Info/AboutNettgrav'));
const EndOfTimeCenter = lazy(() => import('./Info/EndOfTimeCenter'));
const EndOfTimeChat = lazy(() => import('./Info/EndOfTimeChat'));
const CreateSpark = lazy(() => import('./Info/CreateSpark'));
const MyLightInfo = lazy(() => import('./Info/MyLight'));
const Partner = lazy(() => import('./Info/Partner'));
const HowItWorks = lazy(() => import('./Info/HowItWorks'));
const CreateNettgrav = lazy(() => import('./Info/CreateNettgrav'));
const Layout = lazy(() => import('./Layout'));
const NewDeceasedPage = lazy(() => import('./NewDeceasedPage/NewDeceasedPage'));
const NewUserProfile = lazy(() => import('./NewUserProfile/NewUserProfile'));
const Products = lazy(() => import('./Products'));
const ResetPassword = lazy(() => import('./ResetPassword/ResetPassword'));
const Checkout = lazy(() => import('./Store/Checkout'));
const Store = lazy(() => import('./Store/Store'));
const VippsAgreements = lazy(() => import('./VippsAgreements/VippsAgreements'));
const DesignTokens = lazy(() => import('./DesignSystem/DesignTokens'));
const DSShowcase = lazy(() => import('./DesignSystem/Showcase'));
const ErrorTrigger = lazy(() => import('./Dev/ErrorTrigger'));
const GroupModerationInbox = lazy(() => import('./Admin/GroupModerationInbox'));
const GettingStarted = lazy(() => import('./GettingStarted/GettingStarted'));
const GravesHub = lazy(() => import('./GravesHub/GravesHub.jsx'));
const RoomsIndexPage = lazy(() => import('./Rooms/RoomsIndexPage'));
const RoomInviteAcceptPage = lazy(() => import('./Rooms/RoomInviteAcceptPage'));
const RoomRoutePage = lazy(() => import('./Rooms/RoomRoutePage'));
const CreateRoomPage = lazy(() => import('./Rooms/CreateRoomPage'));
const MessagesIndexPage = lazy(() => import('./messages'));
const MessengerV2GroupInviteAcceptPage = lazy(() => import('./messages/group-invite'));
const MessageThreadPage = lazy(() => import('./messages/[id]'));
const MessengerV2CallPage = lazy(() => import('../features/messenger-v2/MessengerV2CallPage.jsx'));
const SparksPage = lazy(() =>
  import('./Sparks/SparksPage.jsx').then((m) => ({ default: m.SparksPage })),
);

const AppLoadingFallback = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      padding: '2rem',
      fontFamily: 'var(--font-sans, Inter, system-ui, -apple-system)',
    }}
    aria-busy="true"
    aria-live="polite"
  >
    <Spinner size={24} /> <span>Loading....</span>
  </div>
);

const FindGraveRoute = () => <HomepageFindGrave />;

// Legacy → Canonical: redirect to /graves/:slug
const RedirectToGrave = () => {
  const { slug } = useParams();
  return <Navigate to={`/graves/${slug}`} replace />;
};

const RedirectToMember = () => {
  const { slug } = useParams();
  return <Navigate to={`/members/${slug}`} replace />;
};

const RedirectRoomDetailsToDefaultTab = () => {
  const { id } = useParams();
  const roomsBasePath = getRoomsBasePath();
  return <Navigate to={`${roomsBasePath}/${id}/${getRoomTabRoute('members_chat')}`} replace />;
};

const RedirectLegacyRoomTab = () => {
  const { id, tab } = useParams();
  const roomsBasePath = getRoomsBasePath();
  const target = resolveRoomLegacyTabTarget(tab);
  return (
    <Navigate
      to={
        target
          ? `${roomsBasePath}/${id}/${target}`
          : `${roomsBasePath}/${id}/${getRoomTabRoute('members_chat')}`
      }
      replace
    />
  );
};

const RedirectLocalizedRoomAlias = () => {
  const location = useLocation();
  const target = buildCanonicalRoomAliasPath(location);
  return target ? <Navigate to={target} replace /> : <NotFoundPage />;
};

const FamilyTreeRoute = () => {
  const isAuth = useSelector((state) => state.auth.isAuthorized);
  return isAuth ? <FamilyTree /> : <FamilyTreeGuestIntro />;
};

const CreateGraveRoute = () => {
  const isAuth = useSelector((state) => state.auth.isAuthorized);
  const location = useLocation();

  if (isAuth) return <CreateNewGrave />;

  const next = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  return <Navigate replace to={`/sign-in?next=${encodeURIComponent(next)}`} />;
};

// Legacy deep links → canonical with context
const RedirectLegacyMemory = () => {
  const { slug, id } = useParams();
  return <Navigate to={`/graves/${slug}?memory=${id}`} replace />;
};
const RedirectLegacyEvent = () => {
  const { slug, id } = useParams();
  return <Navigate to={`/graves/${slug}?tab=events&event_id=${id}`} replace />;
};
const RedirectLegacyPhotos = () => {
  const { slug, id } = useParams();
  const suffix = id ? `?tab=photos&id=${id}` : `?tab=photos`;
  return <Navigate to={`/graves/${slug}${suffix}`} replace />;
};

const RedirectLegacyGraveInvitation = () => {
  const { slug } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || params.get('invitation_token');
  const nextPath = `/graves/${slug}`;

  if (token) {
    return (
      <Navigate
        to={`/register?token=${encodeURIComponent(token)}&next=${encodeURIComponent(nextPath)}`}
        replace
      />
    );
  }

  return <Navigate to={nextPath} replace />;
};

const LegacyStoreRedirect = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

const RedirectFindGravePathAlias = ({ fromBase, toBase }) => {
  const location = useLocation();
  const targetPath =
    location.pathname === fromBase
      ? toBase
      : location.pathname.replace(new RegExp(`^${fromBase}(?=/)`), toBase);

  return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />;
};

const footerAliasPaths = (pageKey) => {
  const slugs = FOOTER_PAGE_SLUGS[pageKey] || {};
  return [
    ...new Set(
      Object.values(slugs)
        .filter(Boolean)
        .map((slug) => `/${slug}`),
    ),
  ];
};

const OverlayRouteChangeHandler = () => {
  const location = useLocation();
  useEffect(() => {
    try {
      closeAllOverlays();
    } catch {}
  }, [location.key]);
  return null;
};

const LayoutRoutes = () => {
  const findGrave = getFindGraveSlug();
  const roomsBasePath = getRoomsBasePath();
  const roomsCreatePath = getRoomsCreatePath();
  const roomsAcceptPath = getRoomsAcceptPath();
  const roomMembersChatRoute = getRoomTabRoute('members_chat');
  const roomAliasBasePaths = getRoomBasePathAliases().filter((path) => path !== roomsBasePath);
  const isAuth = useSelector((state) => state.auth.isAuthorized);
  const getUserSlug = () =>
    localStorage.getItem('user-slug') || sessionStorage.getItem('user-slug');
  return (
    <Suspense fallback={<AppLoadingFallback />}>
      <Layout>
        <Routes>
          <Route
            path="/"
            element={
              <Suspense fallback={<AppLoadingFallback />}>
                <Homepage />
              </Suspense>
            }
          />
          <Route path="/graven" element={<HomepageGrave />} />
          <Route path="/invest" element={<HomepageInvestigate />} />
          <Route path="/scientists" element={<HomepageScientists />} />
          <Route
            path="/sparks"
            element={
              <Suspense fallback={null}>
                <SparksPage />
              </Suspense>
            }
          />
          <Route
            path="/finn-grav/*"
            element={<RedirectFindGravePathAlias fromBase="/finn-grav" toBase="/finn-en-grav" />}
          />
          <Route
            path="/hitta-grav/*"
            element={<RedirectFindGravePathAlias fromBase="/hitta-grav" toBase="/hitta-en-grav" />}
          />
          <Route
            path="/find-grav/*"
            element={<RedirectFindGravePathAlias fromBase="/find-grav" toBase="/find-en-grav" />}
          />
          <Route
            path="/find-grave/*"
            element={<RedirectFindGravePathAlias fromBase="/find-grave" toBase="/find-a-grave" />}
          />
          {findGrave !== 'finn-en-grav' && (
            <Route path="/finn-en-grav/*" element={<Navigate to={`/${findGrave}`} replace />} />
          )}
          <Route path={`/${findGrave}`} element={<FindGraveRoute />} />
          <Route path={`/${findGrave}/:municipality`} element={<FindGraveRoute />} />
          <Route path={`/${findGrave}/:municipality/:cemetery`} element={<FindGraveRoute />} />
          <Route
            path={`/${findGrave}/:municipality/:cemetery/:slug`}
            element={<RedirectToGrave />}
          />
          <Route path="/graveier" element={<LearnMore />} />
          {/* Canonical legal route */}
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          {footerAliasPaths('about').map((path) => (
            <Route key={path} path={path} element={<AboutNettgrav />} />
          ))}
          {footerAliasPaths('endOfTimeCenter').map((path) => (
            <Route key={path} path={path} element={<EndOfTimeCenter />} />
          ))}
          {footerAliasPaths('endOfTimeChat').map((path) => (
            <Route key={path} path={path} element={<EndOfTimeChat />} />
          ))}
          {footerAliasPaths('createNettgrav').map((path) => (
            <Route key={path} path={path} element={<CreateNettgrav />} />
          ))}
          {footerAliasPaths('createSpark').map((path) => (
            <Route key={path} path={path} element={<CreateSpark />} />
          ))}
          {footerAliasPaths('myLight').map((path) => (
            <Route key={path} path={path} element={<MyLightInfo />} />
          ))}
          {footerAliasPaths('partner').map((path) => (
            <Route key={path} path={path} element={<Partner />} />
          ))}
          {footerAliasPaths('howItWorks').map((path) => (
            <Route key={path} path={path} element={<HowItWorks />} />
          ))}
          <Route path="/design-system" element={<DesignTokens />} />
          <Route
            path="/getting-started"
            element={
              <PrivateRoute>
                <GettingStarted />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/design-system"
            element={
              <AdminRoute>
                <DSShowcase />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/error-trigger"
            element={
              <AdminRoute>
                <ErrorTrigger />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/group-moderation"
            element={
              <AdminRoute>
                <GroupModerationInbox />
              </AdminRoute>
            }
          />
          {/* Canonical auth routes */}
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/register" element={<Registration />} />
          <Route
            path="/create_grave"
            element={
              <PrivateRoute>
                <CreateNewGrave />
              </PrivateRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPageTabs />
              </PrivateRoute>
            }
          >
            <Route index element={<SettingsPersonalInformation />} />
            <Route path="contact" element={<Contact />} />
            <Route path="payments" element={<Payments />} />
            <Route path="payment-methods" element={<PaymentMethods />} />
            <Route path="subscription" element={<CureSubscription />} />
            <Route path="subscriptions" element={<Navigate to="/settings" replace />} />
            <Route path="plan-members" element={<Navigate to="/settings" replace />} />
            <Route path="subscription-invitations" element={<Navigate to="/settings" replace />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="account-information" element={<UserAccount />} />
            <Route path="security" element={<SecuritySessions />} />
            {/* <Route path="inventory" element={<Inventory />} /> */}
          </Route>
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <NotificationsPage />
              </PrivateRoute>
            }
          />
          {/* Memorials (canonical) */}
          <Route path="/graves/:slug" element={<NewDeceasedPage />}>
            <Route index element={<DeceasedGraveWall />} />
          </Route>
          <Route path="/graves/new" element={<CreateGraveRoute />} />
          {/* Legacy memorial path → canonical */}
          <Route path="/deceased/:slug" element={<RedirectToGrave />} />
          {/* Optional: preserve deep legacy context */}
          <Route path="/deceased/:slug/memory/:id" element={<RedirectLegacyMemory />} />
          <Route path="/deceased/:slug/event/:id" element={<RedirectLegacyEvent />} />
          <Route path="/deceased/:slug/photos" element={<RedirectLegacyPhotos />} />
          <Route path="/deceased/:slug/photos/:id" element={<RedirectLegacyPhotos />} />
          <Route path="/deceased/:slug/*" element={<RedirectToGrave />} />

          {/* Members (canonical) */}
          <Route
            path="/members/me"
            element={
              isAuth ? (
                <Navigate replace to={`/members/${getUserSlug()}`} />
              ) : (
                <Navigate replace to={`/sign-in?next=/members/me`} />
              )
            }
          />
          <Route
            path="/members/:slug"
            element={
              <PrivateRoute>
                <NewUserProfile />
              </PrivateRoute>
            }
          />
          {/* Legacy singular member path → canonical */}
          <Route path="/member/:slug" element={<RedirectToMember />} />
          {/* Mitt-lys public alias to member slug (keeps same page for now) */}
          <Route path="/mitt-lys/:slug" element={<RedirectToMember />} />
          <Route path="/family-tree" element={<FamilyTreeRoute />} />
          <Route
            path="/family-tree/:id"
            element={
              <PrivateRoute>
                <FamilyTree />
              </PrivateRoute>
            }
          />
          {/* Search (canonical) */}
          <Route path="/search" element={<Graves />} />
          <Route path="/search/graves" element={<Graves />} />
          <Route path="/search/people" element={<Graves />} />
          <Route path="/graves" element={<GravesHub />} />
          <Route path="/pricing" element={<Navigate to="/" replace />} />
          <Route path="/pricing/payment" element={<Navigate to="/" replace />} />
          <Route path="/store" element={<Store />} />
          <Route path="/store/checkout" element={<Checkout />} />
          {/* <Route path="/store" element={<LegacyStoreRedirect to="/store" />} />
          <Route path="/store/checkout" element={<LegacyStoreRedirect to="/store/checkout" />} /> */}
          <Route
            path="/vipps-payment-confirmation"
            element={<Navigate to="/settings/payment-methods" replace />}
          />
          <Route
            path="/vipps-agreements"
            element={
              <PrivateRoute>
                <VippsAgreements />
              </PrivateRoute>
            }
          />
          {/* Legacy grave subpaths → canonical */}
          <Route
            path="/grave/:slug/claim"
            element={<Navigate to="/graves/:slug/claim" replace />}
          />
          <Route path="/grave/:slug/invitation" element={<RedirectLegacyGraveInvitation />} />
          <Route path="/create_grave" element={<Navigate to="/graves/new" replace />} />
          <Route path="/account-confirmation" element={<AccountConfirmation />} />
          <Route path="/moon_request" element={<MoonRequestPage />} />
          <Route
            path="/product/new"
            element={
              <AdminRoute>
                <Products />
              </AdminRoute>
            }
          />
          {/* Death notices (canonical) */}
          <Route
            path="/death-notices"
            element={<Navigate to="/graves/new?mode=with_ceremony" replace />}
          />
          <Route
            path="/death-notices/new"
            element={<Navigate to="/graves/new?mode=with_ceremony" replace />}
          />
          <Route path="/death-notices/:slug" element={<PublicNotice />} />
          {/* Legacy death notices paths → canonical */}
          <Route path="/death_notices" element={<Navigate to="/death-notices" replace />} />
          <Route path="/death_notices/new" element={<Navigate to="/death-notices/new" replace />} />
          <Route path="/death_notices/:slug" element={<PublicNotice />} />
          <Route path="/funeral/:slug" element={<FuneralRoom />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Rooms */}
          <Route path={roomsBasePath} element={<RoomsIndexPage />} />
          <Route
            path={roomsCreatePath}
            element={
              <PrivateRoute>
                <CreateRoomPage />
              </PrivateRoute>
            }
          />
          <Route path={roomsAcceptPath} element={<RoomInviteAcceptPage />} />
          <Route
            path={`${roomsBasePath}/:id/calls/:callId`}
            element={
              <PrivateRoute>
                <MessengerV2CallPage />
              </PrivateRoute>
            }
          />
          <Route
            path={`${roomsBasePath}/:id`}
            element={
              <PrivateRoute>
                <RoomRoutePage />
              </PrivateRoute>
            }
          >
            <Route index element={<RedirectRoomDetailsToDefaultTab />} />
            <Route path={`${roomMembersChatRoute}/*`} />
            <Route path="info/:section?" />
            <Route path=":tab" element={<RedirectLegacyRoomTab />} />
            <Route path="*" element={<RedirectLocalizedRoomAlias />} />
          </Route>
          {roomAliasBasePaths.map((aliasBasePath) => (
            <Route
              key={aliasBasePath}
              path={`${aliasBasePath}/*`}
              element={<RedirectLocalizedRoomAlias />}
            />
          ))}
          <Route
            path="/calls/:callId"
            element={
              <PrivateRoute>
                <MessengerV2CallPage />
              </PrivateRoute>
            }
          />
          <Route
            path={MESSENGER_V2_GROUP_INVITE_ACCEPT_PATH}
            element={<MessengerV2GroupInviteAcceptPage />}
          />
          <Route
            path="/messages/groups/:id/forward/:forwardMessageId"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/groups/:id/info/:section?"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/groups/:id/media/:messageId/:index?"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/groups/:id/attachments/:draftId/edit/:tool?/:layerId?"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/groups/:id/attachments/:draftId"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/groups/:id"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/groups"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:id/forward/:forwardMessageId"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:id/info/:section?"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:id/media/:messageId/:index?"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:id/attachments/:draftId/edit/:tool?/:layerId?"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:id/attachments/:draftId"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:id?"
            element={
              <PrivateRoute>
                <MessageThreadPage />
              </PrivateRoute>
            }
          />
          {/* Legacy auth & terms paths → canonical */}
          <Route path="/auth/sign-in" element={<Navigate to="/sign-in" replace />} />
          <Route path="/registration" element={<Navigate to="/register" replace />} />
          <Route path="/terms_conditions" element={<Navigate to="/terms" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Layout>
    </Suspense>
  );
};

const AppRouter = () => {
  return (
    <BrowserRouter>
      <OverlayRouteChangeHandler />
      <FirstPartyPageview />
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/*" element={<LayoutRoutes />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
